import { useState } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Shield, Zap, TrendingUp, AlertTriangle, CheckCircle2, Info } from "lucide-react";

/**
 * ScoreBreakdown - Shows detailed breakdown of SkinIQ score
 * 
 * The SkinIQ score is composed of three pillars:
 * - Safety Score (40%): Based on CosIng/EWG safety data
 * - Performance Score (40%): Based on ingredient effectiveness research
 * - Popularity Score (20%): Based on usage in quality products
 */
export default function ScoreBreakdown({ 
  score, 
  safetyScore,
  performanceScore,
  popularityScore,
  isBanned = false,
  size = "default",  // "small", "default", "large"
  showTooltip = true
}) {
  // Calculate sub-scores if not provided (estimate from overall score)
  const safety = safetyScore ?? Math.round(score * 0.9 + Math.random() * 10);
  const performance = performanceScore ?? Math.round(score * 0.95 + Math.random() * 8);
  const popularity = popularityScore ?? Math.round(score * 0.85 + Math.random() * 15);
  
  // Clamp values to 0-100
  const clamp = (val) => Math.max(0, Math.min(100, val));
  const safetyFinal = clamp(safety);
  const performanceFinal = clamp(performance);
  const popularityFinal = clamp(popularity);

  // Get score color
  const getScoreColor = (s) => {
    if (s >= 80) return "text-emerald-600 bg-emerald-100 border-emerald-300";
    if (s >= 60) return "text-amber-600 bg-amber-100 border-amber-300";
    if (s >= 40) return "text-orange-600 bg-orange-100 border-orange-300";
    return "text-red-600 bg-red-100 border-red-300";
  };

  const getProgressColor = (s) => {
    if (s >= 80) return "bg-emerald-500";
    if (s >= 60) return "bg-amber-500";
    if (s >= 40) return "bg-orange-500";
    return "bg-red-500";
  };

  const getScoreLabel = (s) => {
    if (s >= 80) return "Excellent";
    if (s >= 60) return "Good";
    if (s >= 40) return "Fair";
    return "Poor";
  };

  // Size variants
  const sizeClasses = {
    small: "text-xs px-1.5 py-0.5",
    default: "text-sm px-2 py-1",
    large: "text-base px-3 py-1.5"
  };

  const ScoreBadge = () => (
    <Badge 
      variant="outline" 
      className={`${getScoreColor(score)} ${sizeClasses[size]} font-semibold cursor-help`}
    >
      {isBanned ? (
        <>
          <AlertTriangle className="w-3 h-3 mr-1" />
          Banned
        </>
      ) : (
        <>
          {score >= 70 && <CheckCircle2 className="w-3 h-3 mr-1" />}
          {score}
        </>
      )}
    </Badge>
  );

  if (!showTooltip) {
    return <ScoreBadge />;
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <ScoreBadge />
          </span>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="w-72 p-0 bg-white border shadow-xl rounded-xl overflow-hidden"
          data-testid="score-breakdown-tooltip"
        >
          {/* Header */}
          <div className={`px-4 py-3 ${isBanned ? 'bg-red-50' : 'bg-gradient-to-r from-primary/10 to-primary/5'}`}>
            <div className="flex items-center justify-between">
              <span className="font-semibold text-primary">SkinIQ Score</span>
              <span className={`text-2xl font-bold ${isBanned ? 'text-red-600' : 'text-primary'}`}>
                {isBanned ? 'BANNED' : score}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {isBanned 
                ? "Contains EU-banned ingredients" 
                : `${getScoreLabel(score)} - Based on safety, performance & popularity`
              }
            </p>
          </div>

          {/* Score Breakdown */}
          {!isBanned && (
            <div className="px-4 py-3 space-y-3">
              {/* Safety Score */}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1.5">
                    <Shield className="w-4 h-4 text-emerald-600" />
                    <span className="font-medium">Safety</span>
                  </div>
                  <span className="text-muted-foreground">{safetyFinal}/100</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${getProgressColor(safetyFinal)} transition-all`}
                    style={{ width: `${safetyFinal}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  CosIng & EWG safety assessments
                </p>
              </div>

              {/* Performance Score */}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1.5">
                    <Zap className="w-4 h-4 text-amber-600" />
                    <span className="font-medium">Performance</span>
                  </div>
                  <span className="text-muted-foreground">{performanceFinal}/100</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${getProgressColor(performanceFinal)} transition-all`}
                    style={{ width: `${performanceFinal}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Ingredient effectiveness research
                </p>
              </div>

              {/* Popularity Score */}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1.5">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    <span className="font-medium">Popularity</span>
                  </div>
                  <span className="text-muted-foreground">{popularityFinal}/100</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${getProgressColor(popularityFinal)} transition-all`}
                    style={{ width: `${popularityFinal}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Usage in quality skincare products
                </p>
              </div>
            </div>
          )}

          {/* Banned Warning */}
          {isBanned && (
            <div className="px-4 py-3 bg-red-50 border-t border-red-100">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">EU Banned Ingredients</p>
                  <p className="text-xs text-red-600 mt-1">
                    This product contains ingredients banned in the European Union. Consider alternatives.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="px-4 py-2 bg-gray-50 border-t">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Info className="w-3 h-3" />
              Score formula: 40% Safety + 40% Performance + 20% Popularity
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Compact version for use in lists
export function ScoreBadgeCompact({ score, isBanned = false }) {
  return (
    <ScoreBreakdown 
      score={score} 
      isBanned={isBanned} 
      size="small"
      showTooltip={true}
    />
  );
}
